import React, { useState } from "react";
import styled from "styled-components/macro";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import { makeStyles } from "@material-ui/core/styles";
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@material-ui/core";
import { theme } from "./theme";

/**
 * Mandatory cancel-reason picker for CS dashboard cancels.
 * reasons: [{_id, title}, ...]
 */
const CancelReasonModal = ({ open, onClose, onConfirm, reasons, title }) => {
  const classes = useStyles();
  const [reasonId, setReasonId] = useState("");

  const handleConfirm = () => {
    if (!reasonId) return;
    onConfirm(reasonId);
    setReasonId("");
  };

  const handleClose = () => {
    setReasonId("");
    onClose();
  };

  return (
    <Modal
      className={classes.modal}
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Card>
        <Heading>{title || "Cancel print job"}</Heading>
        <Hint>A cancellation reason is required.</Hint>
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel id="cancel-reason-label">Reason</InputLabel>
          <Select
            labelId="cancel-reason-label"
            value={reasonId}
            label="Reason"
            onChange={(e) => setReasonId(e.target.value)}
          >
            {(reasons || []).map((r) => (
              <MenuItem key={r._id} value={r._id}>
                {r.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Actions>
          <Button onClick={handleClose} style={{ textTransform: "none", color: theme.textMuted }}>
            Back
          </Button>
          <Button
            variant="contained"
            disabled={!reasonId}
            onClick={handleConfirm}
            style={{
              textTransform: "none",
              borderRadius: theme.radiusSm,
              backgroundColor: reasonId ? theme.danger : undefined,
              color: reasonId ? "#ffffff" : undefined,
              boxShadow: "none",
            }}
          >
            Confirm cancel
          </Button>
        </Actions>
      </Card>
    </Modal>
  );
};

/**
 * Simple user search for dashboard create-print (backed by /searchUserByDynamic).
 */
const UserSearchField = ({ selectedUser, onSelect, onSearch, results, searching }) => {
  const [query, setQuery] = useState("");

  return (
    <UserSearchWrapper>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        label="Search user (name / email / phone / id)"
        value={query}
        onChange={(e) => {
          const value = e.target.value;
          setQuery(value);
          onSearch(value);
        }}
      />
      {selectedUser ? (
        <Selected>
          Selected: {selectedUser.fullName || selectedUser.email || selectedUser.user}
          <ClearBtn type="button" onClick={() => onSelect(null)}>
            Clear
          </ClearBtn>
        </Selected>
      ) : null}
      {!selectedUser && (results || []).length > 0 ? (
        <Results>
          {results.map((u) => (
            <ResultItem key={u.user} type="button" onClick={() => onSelect(u)}>
              <strong>{u.fullName || "—"}</strong>
              <span>{u.email}</span>
              <span>{u.phone || ""}</span>
            </ResultItem>
          ))}
        </Results>
      ) : null}
      {searching ? <Hint>Searching…</Hint> : null}
    </UserSearchWrapper>
  );
};

const useStyles = makeStyles(() => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
  },
}));

const Card = styled.div`
  background: ${theme.surface};
  border-radius: ${theme.radius};
  box-shadow: ${theme.shadowHover};
  padding: 24px;
  min-width: 380px;
  outline: none;
  font-family: ${theme.fontFamily};
`;

const Heading = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: ${theme.text};
  margin-bottom: 8px;
`;

const Hint = styled.div`
  font-size: 13px;
  color: ${theme.textMuted};
  margin: 8px 0 12px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

const UserSearchWrapper = styled.div`
  margin: 10px 0;
  font-family: ${theme.fontFamily};
`;

const Selected = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: ${theme.primary};
`;

const ClearBtn = styled.button`
  margin-left: 8px;
  border: none;
  background: transparent;
  color: ${theme.danger};
  cursor: pointer;
  font-family: ${theme.fontFamily};
`;

const Results = styled.div`
  max-height: 160px;
  overflow: auto;
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  margin-top: 6px;
`;

const ResultItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  border: none;
  border-bottom: 1px solid ${theme.border};
  background: ${theme.surface};
  padding: 8px 10px;
  cursor: pointer;
  text-align: left;
  font-family: ${theme.fontFamily};
  &:hover {
    background: ${theme.background};
  }
  span {
    font-size: 12px;
    color: ${theme.textMuted};
  }
`;

export { CancelReasonModal, UserSearchField };
